/**
 * Public Spotted intake page — Phase C.
 *
 * Reachable without login at `/intake/:token`. Used by non-members
 * (customers, partners, vendors) to submit spots into a workspace.
 * The token is the credential; the workspace name is shown but no
 * other workspace details leak.
 *
 * Submissions land in the same `submitSpot` pipeline as logged-in
 * intake, so XAMI classification + auto-approve + outcome posting all
 * apply uniformly.
 */

import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ApiError} from "../api/client";
import { apiGet, apiPost } from "../api/client";

interface IntakeSchema {
  label: string;
  workspace: { name: string };
  kinds: string[];
  impacts: string[];
}

const KIND_LABELS: Record<string, string> = {
  risk: "Risk",
  opportunity: "Opportunity",
  client_request: "Request",
  operational: "Operational",
  other: "Other",
};

export function ExternalIntakePage() {
  const { token } = useParams<{ token: string }>();
  const schemaQ = useQuery<IntakeSchema, ApiError>({
    queryKey: ["intake-schema", token],
    queryFn: () => apiGet<IntakeSchema>(`/api/intake/${token}`),
    enabled: !!token,
    retry: false,
  });

  const [reporterName, setReporterName] = useState("");
  const [reporterContact, setReporterContact] = useState("");
  const [kind, setKind] = useState<string>("operational");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [impact, setImpact] = useState<string>("");
  const [clientTag, setClientTag] = useState("");

  const submitM = useMutation<{ ok: boolean; spotId: string; message: string }, ApiError, void>({
    mutationFn: () =>
      apiPost(`/api/intake/${token}`, {
        reporter_name: reporterName.trim(),
        ...(reporterContact.trim() && { reporter_contact: reporterContact.trim() }),
        kind,
        title: title.trim(),
        description: description.trim(),
        ...(impact && { impact_estimate: impact }),
        ...(clientTag.trim() && { client_tag: clientTag.trim() }),
      }),
  });

  if (!token) {
    return (
      <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
        <div className="review-page" style={{ margin: "0 auto" }}>
          <div className="review-banner error">Missing intake link.</div>
        </div>
      </main>
    );
  }

  if (schemaQ.isLoading) {
    return (
      <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
        <div className="review-page" style={{ margin: "0 auto" }}>Loading…</div>
      </main>
    );
  }

  if (schemaQ.error) {
    const status = schemaQ.error.status;
    const message =
      status === 404 ? "This intake link is invalid or has been revoked."
      : status === 410 ? "This intake link has expired or was revoked."
      : status === 429 ? "This intake link has reached its submission cap."
      : `Could not load the intake form (${status}).`;
    return (
      <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
        <div className="review-page" style={{ margin: "0 auto" }}>
          <div className={`review-banner ${status === 410 || status === 429 ? "warn" : "error"}`}>
            {message}
          </div>
        </div>
      </main>
    );
  }

  const schema = schemaQ.data;
  if (!schema) return null;

  if (submitM.isSuccess) {
    return (
      <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
        <div className="review-page" style={{ margin: "0 auto", maxWidth: 640 }}>
          <p className="review-eyebrow">{schema.workspace.name}</p>
          <h1 className="review-title">Thanks — we got it</h1>
          <div className="review-banner ok" style={{ marginTop: 16 }}>
            {submitM.data?.message ?? "Your submission was received."}
          </div>
          <p style={{ marginTop: 24, fontSize: 13, color: "var(--text-2, #475569)" }}>
            The team has been notified. If you need to add more context, reach out via the
            channel you got this link from.
          </p>
        </div>
      </main>
    );
  }

  const canSubmit =
    reporterName.trim().length >= 1 &&
    title.trim().length >= 2 &&
    description.trim().length >= 2 &&
    !submitM.isPending;

  return (
    <main style={{ minHeight: "100vh", background: "var(--bg-canvas, #f8fafc)" }}>
      <div className="review-page" style={{ margin: "0 auto", maxWidth: 640 }}>
        <p className="review-eyebrow">{schema.workspace.name}</p>
        <h1 className="review-title">Report a spot</h1>
        <p className="review-meta">
          {schema.label
            ? <>You're submitting via <strong>{schema.label}</strong>.</>
            : null}{" "}
          Tell us what you saw and we'll route it to the right person. You don't need an
          account.
        </p>

        <section className="review-form" style={{ marginTop: 18 }}>
          <label className="review-field">
            <span className="review-field-label">
              <strong>Your name</strong>
            </span>
            <input
              className="review-input"
              type="text"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              placeholder="So the team knows who reported it"
            />
          </label>

          <label className="review-field">
            <span className="review-field-label">
              <strong>Contact (optional)</strong>
              <span className="review-field-hint">email or phone if you'd like a reply</span>
            </span>
            <input
              className="review-input"
              type="text"
              value={reporterContact}
              onChange={(e) => setReporterContact(e.target.value)}
              placeholder="alex@example.com"
            />
          </label>

          <div className="spotted-form-row" style={{ marginTop: 12 }}>
            <label className="spotted-field">
              <span>Kind</span>
              <select
                className="review-input"
                value={kind}
                onChange={(e) => setKind(e.target.value)}
              >
                {schema.kinds.map((k) => (
                  <option key={k} value={k}>{KIND_LABELS[k] ?? k}</option>
                ))}
              </select>
            </label>
            <label className="spotted-field">
              <span>Impact (optional)</span>
              <select
                className="review-input"
                value={impact}
                onChange={(e) => setImpact(e.target.value)}
              >
                <option value="">—</option>
                {schema.impacts.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="review-field">
            <span className="review-field-label">
              <strong>Title</strong>
            </span>
            <input
              className="review-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="One line — what did you notice?"
            />
          </label>

          <label className="review-field">
            <span className="review-field-label">
              <strong>Description</strong>
            </span>
            <textarea
              className="review-textarea"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What did you see, when, and any specifics that would help."
            />
          </label>

          <label className="review-field">
            <span className="review-field-label">
              <strong>Client / account (optional)</strong>
            </span>
            <input
              className="review-input"
              type="text"
              value={clientTag}
              onChange={(e) => setClientTag(e.target.value)}
              placeholder="e.g. Northwind Logistics"
            />
          </label>

          {submitM.error && (
            <div className="review-banner error" style={{ marginTop: 12 }}>
              {submitM.error.status === 410
                ? "This intake link expired before we could record your submission."
                : submitM.error.status === 429
                ? "This intake link has reached its submission cap."
                : `Could not submit (${submitM.error.status}). Please try again.`}
            </div>
          )}

          <div className="review-actions" style={{ marginTop: 16 }}>
            <button
              type="button"
              className="review-btn approve"
              disabled={!canSubmit}
              onClick={() => submitM.mutate()}
            >
              {submitM.isPending ? "Submitting…" : "Submit"}
            </button>
          </div>
        </section>

        <p style={{ marginTop: 24, fontSize: 11, color: "var(--text-3, #94a3b8)" }}>
          PlayStudio · {schema.workspace.name}. Your submission goes only to this workspace's team.
        </p>
      </div>
    </main>
  );
}
